import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CHAPTERS } from "../data/chapters";
import ProofReaction from "./ProofReaction";
import EngineReport, { EngineSummary } from "./EngineReport";
import ReviewQueue from "./ReviewQueue";
import { SessionCompleteButton, VipPackageForm } from "./InPersonTools";
import SessionLogPanel, { HighlightUploadButton, HomeworkPanel } from "./coach/SessionLogPanel";
import TechnicalRatings from "./coach/TechnicalRatings";
import DossierRelease from "./coach/DossierRelease";
import { Users, ChevronRight, CheckCircle2, Clock, AlertCircle, X, Footprints, EyeOff, Eye, Trophy, Flame, Link2, RotateCcw, Crown, Save, Search, ListChecks, Square, CheckSquare, Award, ChevronDown, Pin, PinOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS = {
  pending: { label: "Pending", color: "#EAB308", icon: Clock },
  approved: { label: "Approved", color: "#22C55E", icon: CheckCircle2 },
  needs_work: { label: "Needs Work", color: "#C41E24", icon: AlertCircle },
};

const relTime = (iso) => {
  if (!iso) return "Never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "Never";
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000), h = Math.floor(diff / 3600000), d = Math.floor(diff / 86400000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

export default function CoachPlayers({ token, openPlayerId, onPlayerOpened, returnTo, onReturn, initialFilter, onFilterConsumed, initialSort, onSortConsumed }) {
  const [players, setPlayers] = useState([]);
  const [board, setBoard] = useState([]);
  const [active, setActive] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent_upload");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showQueue, setShowQueue] = useState(false);
  const [bonusOpen, setBonusOpen] = useState(false);
  const [bonusVal, setBonusVal] = useState(25);
  const [showBoard, setShowBoard] = useState(false);
  const [showPlayers, setShowPlayers] = useState(false);
  const [deepLinked, setDeepLinked] = useState(false);
  const [boardQuery, setBoardQuery] = useState("");
  const [engineOpen, setEngineOpen] = useState(null);
  const [talkPoint, setTalkPoint] = useState({});

  const startVoiceFromCue = (pr, text) => {
    const ta = document.getElementById(`note-${pr.id}`);
    if (ta) ta.value = text;
    setTalkPoint((m) => ({ ...m, [pr.id]: text }));
  };
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = () => axios.get(`${API}/coach/players`, auth).then(({ data }) => setPlayers(data)).catch(() => {});
  const loadBoard = () => axios.get(`${API}/leaderboard?limit=50`, auth).then(({ data }) => setBoard(data.top || [])).catch(() => {});
  useEffect(() => { load(); loadBoard(); /* eslint-disable-next-line */ }, []);

  const open = async (id) => {
    // Fetch O-Rank alongside the detail: once the modal's proof <video>s start streaming they saturate the browser's per-host connection pool.
    const [{ data }, orank] = await Promise.all([
      axios.get(`${API}/coach/players/${id}`, auth),
      axios.get(`${API}/coach/players/${id}/orank`, auth).then((r) => r.data).catch(() => null),
    ]);
    setActive({ ...data, orank });
  };

  // Close the detail modal — if it was opened via a deep-link (e.g. from the
  // Club/Team leaderboard), return the coach to exactly where they came from.
  const closeDetail = () => {
    setActive(null);
    if (deepLinked && returnTo && onReturn) onReturn();
    setDeepLinked(false);
  };

  // Deep-link open from the Club View dashboard.
  useEffect(() => {
    if (openPlayerId) {
      setDeepLinked(true);
      open(openPlayerId).catch(() => {});
      onPlayerOpened && onPlayerOpened();
    }
    // eslint-disable-next-line
  }, [openPlayerId]);

  // Landing filter from a Business Pulse tap — auto-expand the list & apply it.
  useEffect(() => {
    if (initialFilter && initialFilter !== "all") {
      setFilter(initialFilter);
      setShowPlayers(true);
      onFilterConsumed && onFilterConsumed();
    }
    // eslint-disable-next-line
  }, [initialFilter]);

  // Landing sort from the Engagement pulse tile — expand the list & sort by Last Active.
  useEffect(() => {
    if (initialSort) {
      setSortBy(initialSort);
      setShowPlayers(true);
      onSortConsumed && onSortConsumed();
    }
    // eslint-disable-next-line
  }, [initialSort]);

  const boardQ = boardQuery.trim().toLowerCase();
  const filteredBoard = boardQ ? board.filter((r) => (r.name || "").toLowerCase().includes(boardQ)) : board;

  const [tierDraft, setTierDraft] = useState("ebook");
  const [greetingDraft, setGreetingDraft] = useState("");
  const [roadmap, setRoadmap] = useState({ month: "", focus: "", schedule: "", summary: "" });
  const [savingTier, setSavingTier] = useState(false);
  useEffect(() => {
    if (active?.player) {
      setTierDraft(active.player.tier || "ebook");
      setGreetingDraft(active.player.elite_video_url || "");
      setRoadmap({
        month: active.player.roadmap_month || "",
        focus: active.player.roadmap_focus || "",
        schedule: active.player.roadmap_schedule || "",
        summary: active.player.roadmap_summary || "",
      });
    }
  }, [active?.player?.id]); // eslint-disable-line

  const saveMembership = async () => {
    setSavingTier(true);
    try {
      const payload = { tier: tierDraft, elite_video_url: tierDraft === "elite" ? greetingDraft : "" };
      if (tierDraft === "elite") {
        payload.roadmap_month = roadmap.month;
        payload.roadmap_focus = roadmap.focus;
        payload.roadmap_schedule = roadmap.schedule;
        payload.roadmap_summary = roadmap.summary;
      }
      const { data } = await axios.put(`${API}/coach/players/${active.player.id}`, payload, auth);
      setActive((prev) => ({ ...prev, player: { ...prev.player, tier: data.tier, elite_video_url: data.elite_video_url,
        roadmap_month: roadmap.month, roadmap_focus: roadmap.focus, roadmap_schedule: roadmap.schedule, roadmap_summary: roadmap.summary } }));
      setPlayers((prev) => prev.map((x) => (x.id === active.player.id ? { ...x, tier: data.tier } : x)));
      toast.success(data.greeting_email_sent ? "Saved — personal greeting email sent to the player." : "Membership & roadmap updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save membership.");
    } finally {
      setSavingTier(false);
    }
  };

  const review = async (proofId, status, note) => {
    await axios.post(`${API}/coach/proofs/${proofId}/review`, { status, coach_note: note || "" }, auth);
    toast.success("Review saved.");
    open(active.player.id);
    load();
  };

  const [copying, setCopying] = useState(null);
  const copySetupLink = async (p) => {
    setCopying(p.id);
    try {
      const { data } = await axios.post(`${API}/coach/claim-link`, { email: p.email }, auth);
      if (data.status === "already_active") {
        toast.info(data.message || "This player has already set up their account.");
        return;
      }
      await navigator.clipboard.writeText(data.claim_url);
      toast.success("Setup link copied to clipboard — paste it to the player.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate a setup link.");
    } finally {
      setCopying(null);
    }
  };

  const [resetting, setResetting] = useState(null);
  const resetUpload = async (p) => {
    setResetting(p.id);
    try {
      await axios.post(`${API}/coach/players/${p.id}/reset-upload-limit`, {}, auth);
      toast.success(`Upload limit reset for ${p.name || p.email} — they can submit another video.`);
      setPlayers((prev) => prev.map((x) => (x.id === p.id ? { ...x, uploads_used: 0, can_upload: true } : x)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset upload limit.");
    } finally {
      setResetting(null);
    }
  };

  const [pinning, setPinning] = useState("");
  const togglePin = async (p) => {
    const pinned = !p.pinned_today;
    setPinning(p.id);
    try {
      await axios.put(`${API}/coach/players/${p.id}/pin`, { pinned }, auth);
      toast.success(pinned ? `${p.name || "Player"} pinned for today's On-Field session.` : "Unpinned from On-Field.");
      setPlayers((prev) => prev.map((x) => (x.id === p.id ? { ...x, pinned_today: pinned } : x)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update pin.");
    }
    setPinning("");
  };

  const toggleLeaderboard = async (p) => {    const hidden = !p.hidden_from_leaderboard;
    try {
      await axios.post(`${API}/coach/players/${p.id}/leaderboard-visibility`, { hidden }, auth);
      toast.success(hidden ? "Removed from leaderboard." : "Restored to leaderboard.");
      setPlayers((prev) => prev.map((x) => (x.id === p.id ? { ...x, hidden_from_leaderboard: hidden } : x)));
      loadBoard();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update leaderboard visibility.");
    }
  };

  const toggleSelect = (id) => setSelected((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const clearSelection = () => setSelected(new Set());

  const bulkReset = async (ids) => {
    setBulkBusy(true);
    try {
      await Promise.all(ids.map((id) => axios.post(`${API}/coach/players/${id}/reset-upload-limit`, {}, auth)));
      toast.success(`Upload limit reset for ${ids.length} player${ids.length > 1 ? "s" : ""}.`);
      clearSelection(); load();
    } catch { toast.error("Some resets failed. Please try again."); }
    finally { setBulkBusy(false); }
  };

  const bulkVisibility = async (ids, hidden) => {
    setBulkBusy(true);
    try {
      await Promise.all(ids.map((id) => axios.post(`${API}/coach/players/${id}/leaderboard-visibility`, { hidden }, auth)));
      toast.success(hidden ? `${ids.length} removed from leaderboard.` : `${ids.length} restored to leaderboard.`);
      setPlayers((prev) => prev.map((x) => (ids.includes(x.id) ? { ...x, hidden_from_leaderboard: hidden } : x)));
      clearSelection(); loadBoard();
    } catch { toast.error("Some updates failed. Please try again."); }
    finally { setBulkBusy(false); }
  };

  const awardBonus = async (ids, points) => {
    const pts = parseInt(points, 10);
    if (!ids.length) { toast.error("Select at least one player."); return; }
    if (!pts) { toast.error("Enter a non-zero point value."); return; }
    setBulkBusy(true);
    try {
      const { data } = await axios.post(`${API}/coach/players/bonus-points`, { player_ids: ids, points: pts }, auth);
      toast.success(`${pts > 0 ? "Awarded" : "Deducted"} ${Math.abs(pts)} pts ${pts > 0 ? "to" : "from"} ${data.updated} player${data.updated > 1 ? "s" : ""}.`);
      setPlayers((prev) => prev.map((x) => (ids.includes(x.id) ? { ...x, bonus_points: (x.bonus_points || 0) + pts } : x)));
      setBonusOpen(false); clearSelection(); loadBoard();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't award points."); }
    finally { setBulkBusy(false); }
  };

  return (
    <div data-testid="coach-players">
      {/* Live leaderboard preview */}
      <div data-testid="coach-leaderboard-preview" className="mb-8">
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-2"><Trophy size={18} className="text-[#C41E24]" /><h2 className="font-headings text-2xl uppercase tracking-wide">Current Leaderboard</h2></div>
          <button data-testid="toggle-leaderboard-accordion" onClick={() => setShowBoard((v) => !v)}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-3.5 py-2.5 rounded-md border border-white/15 text-white/70 hover:text-white hover:border-white/40 transition-colors flex-shrink-0">
            <ChevronDown size={15} className={`transition-transform ${showBoard ? "rotate-180" : ""}`} /> {showBoard ? "Hide" : `Show All (${board.length})`}
          </button>
        </div>
        <p className="text-sm text-white/45 mb-4">Exactly who's ranked right now — the public standings players see. Use Remove below to hide anyone.</p>
        <div className="relative mb-3">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35 pointer-events-none" />
          <input data-testid="board-search-input" value={boardQuery} onChange={(e) => setBoardQuery(e.target.value)}
            placeholder="Search the leaderboard by name…"
            className="w-full bg-[#0d0d0d] border border-white/10 rounded-md pl-9 pr-3 py-2.5 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]" />
        </div>
        {(showBoard || boardQ) && (<div className="bg-[#141414] border border-white/10 rounded-lg overflow-hidden">
          {filteredBoard.length === 0 ? (
            <p className="text-sm text-white/40 p-4">{boardQ ? `No players match "${boardQuery.trim()}".` : "No ranked players yet. Verified players appear here once they start training."}</p>
          ) : (
            filteredBoard.map((r) => {
              const clickable = !!r.id;
              return (
              <div key={r.rank} data-testid={`coach-board-row-${r.rank}`}
                onClick={clickable ? () => open(r.id) : undefined}
                role={clickable ? "button" : undefined} tabIndex={clickable ? 0 : undefined}
                onKeyDown={clickable ? (e) => { if (e.key === "Enter") open(r.id); } : undefined}
                className={`flex items-center gap-3 px-4 py-3 border-b border-white/5 last:border-0 ${clickable ? "cursor-pointer hover:bg-white/5 transition-colors" : ""}`}>
                <div className={`w-7 text-center font-cover text-lg ${r.rank <= 3 ? "text-[#C41E24]" : "text-white/40"}`}>{r.rank}</div>
                <div className="flex-1 min-w-0">
                  <div data-testid={clickable ? `coach-board-open-${r.id}` : undefined} className={`font-semibold text-sm truncate ${clickable ? "hover:text-[#C41E24]" : ""}`}>{r.name}</div>
                  <div className="text-[11px] text-white/45 flex items-center gap-3">
                    <span>{r.chapters} ch</span>
                    <span>{r.proofs} vids</span>
                    {r.streak > 0 && <span className="flex items-center gap-1 text-[#C41E24]"><Flame size={11} /> {r.streak}d</span>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-cover text-xl text-[#C41E24] leading-none">{r.points}</div>
                  <div className="text-[9px] uppercase tracking-wider text-white/40">pts</div>
                </div>
              </div>
              );
            })
          )}
        </div>)}
      </div>

      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2"><Users size={18} className="text-[#C41E24]" /><h2 className="font-headings text-2xl uppercase tracking-wide">Players</h2></div>
        <div className="flex items-center gap-2">
          <button data-testid="toggle-players-accordion" onClick={() => setShowPlayers((v) => !v)}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-3.5 py-2.5 rounded-md border border-white/15 text-white/70 hover:text-white hover:border-white/40 transition-colors">
            <ChevronDown size={15} className={`transition-transform ${showPlayers ? "rotate-180" : ""}`} /> {showPlayers ? "Hide" : `Show All (${players.length})`}
          </button>
          {(() => {
            const n = players.filter((p) => p.needs_review).length;
            return (
              <button data-testid="review-queue-open" onClick={() => setShowQueue(true)}
                className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-3.5 py-2.5 rounded-md transition-colors ${n > 0 ? "bg-[#C41E24] hover:bg-[#A0161D] text-white" : "bg-white/5 border border-white/15 text-white/60 hover:text-white"}`}>
                <ListChecks size={15} /> Review Queue{n > 0 ? ` · ${n}` : ""}
              </button>
            );
          })()}
        </div>
      </div>
      {showQueue && <ReviewQueue token={token} onClose={() => setShowQueue(false)} onDone={load} />}

      {showPlayers && (<>
      {/* Search + filters toolbar */}
      <div className="mb-5 space-y-3" data-testid="players-toolbar">
        <div className="relative">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/35" />
          <input
            data-testid="players-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search players by name or email…"
            className="w-full bg-[#0d0d0d] border border-white/10 rounded-lg pl-10 pr-9 py-3 text-white text-sm placeholder:text-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
          />
          {query && (
            <button data-testid="players-search-clear" onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/35 hover:text-white" aria-label="Clear search"><X size={15} /></button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {[
            { id: "all", label: "All" },
            { id: "needs_review", label: "Needs Review" },
            { id: "trial", label: "On Trial" },
            { id: "elite", label: "Elite" },
            { id: "academy", label: "Technical Track" },
            { id: "ebook", label: "Blueprint" },
          ].map((f) => {
            const activeChip = filter === f.id;
            const count = f.id === "all" ? players.length
              : f.id === "needs_review" ? players.filter((p) => p.needs_review).length
              : f.id === "trial" ? players.filter((p) => p.is_trial).length
              : players.filter((p) => (p.tier || "ebook") === f.id).length;
            const isReview = f.id === "needs_review";
            return (
              <button key={f.id} data-testid={`players-filter-${f.id}`} onClick={() => setFilter(f.id)}
                className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-full border transition-colors ${
                  activeChip
                    ? (isReview ? "bg-[#C41E24] border-[#C41E24] text-white" : "bg-white text-black border-white")
                    : "bg-white/5 border-white/15 text-white/60 hover:text-white hover:border-white/40"}`}>
                {isReview && <span className={`w-1.5 h-1.5 rounded-full ${activeChip ? "bg-white" : "bg-[#C41E24]"}`} />}
                {f.label}
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${activeChip ? "bg-black/15" : "bg-white/10 text-white/50"}`}>{count}</span>
              </button>
            );
          })}
          <div className="ml-auto flex items-center gap-1.5" data-testid="players-sort">
            <span className="text-[10px] font-bold uppercase tracking-widest text-white/40">Sort</span>
            {[{ id: "recent_upload", label: "Recent Upload" }, { id: "last_active", label: "Last Active" }].map((s) => (
              <button key={s.id} data-testid={`players-sort-${s.id}`} onClick={() => setSortBy(s.id)}
                className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-full border transition-colors ${
                  sortBy === s.id ? "bg-[#D4AF37] border-[#D4AF37] text-black" : "bg-white/5 border-white/15 text-white/60 hover:text-white hover:border-white/40"}`}>
                {s.id === "last_active" && <Clock size={11} />}{s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {players.length === 0 && <p className="text-sm text-white/40">No players yet. Profiles appear here after someone enrolls in the Ball Mastery Blueprint.</p>}
      {(() => {
        const q = query.trim().toLowerCase();
        const filtered = players.filter((p) => {
          if (filter === "needs_review" && !p.needs_review) return false;
          if (filter === "trial" && !p.is_trial) return false;
          if (["elite", "academy", "ebook"].includes(filter) && (p.tier || "ebook") !== filter) return false;
          if (q && !((p.name || "").toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q))) return false;
          return true;
        });
        if (players.length > 0 && filtered.length === 0)
          return <p data-testid="players-empty" className="text-sm text-white/40 py-6 text-center">No players match your search or filter.</p>;
        const filteredIds = filtered.map((p) => p.id);
        const allSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
        const selArr = filteredIds.filter((id) => selected.has(id));
        // Household grouping: keep siblings adjacent, rank families by their most recent upload.
        const hOf = (p) => p.household_id || p.id;
        const householdSize = {};
        players.forEach((p) => { const h = hOf(p); householdSize[h] = (householdSize[h] || 0) + 1; });
        const grpBest = {};
        filtered.forEach((p) => { const h = hOf(p), lu = p.last_upload || p.created_at || ""; if (!grpBest[h] || lu > grpBest[h]) grpBest[h] = lu; });
        const ordered = [...filtered].sort((a, b) => {
          if (sortBy === "last_active") return (b.last_active || "").localeCompare(a.last_active || "") || (a.name || "").localeCompare(b.name || "");
          const ha = hOf(a), hb = hOf(b);
          if (ha !== hb) return (grpBest[hb] || "").localeCompare(grpBest[ha] || "") || ha.localeCompare(hb);
          if (!!a.is_child !== !!b.is_child) return a.is_child ? 1 : -1;
          return (b.last_upload || b.created_at || "").localeCompare(a.last_upload || a.created_at || "");
        });
        return (
      <>
        {/* Bulk action bar */}
        <div className="flex items-center flex-wrap gap-3 mb-3" data-testid="bulk-toolbar">
          <button data-testid="bulk-select-all" onClick={() => setSelected(allSelected ? new Set() : new Set(filteredIds))}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white/60 hover:text-white">
            {allSelected ? <CheckSquare size={16} className="text-[#C41E24]" /> : <Square size={16} />} Select All
          </button>
          {selArr.length > 0 && (
            <div className="flex items-center flex-wrap gap-2" data-testid="bulk-actions">
              <span className="text-xs font-bold text-white/70">{selArr.length} selected</span>
              <button data-testid="bulk-bonus-points" onClick={() => setBonusOpen(true)} disabled={bulkBusy}
                className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-2 rounded-sm border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-50"><Award size={13} /> Award Points</button>
              <button data-testid="bulk-reset-upload" onClick={() => bulkReset(selArr)} disabled={bulkBusy}
                className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-2 rounded-sm border border-white/15 text-white/70 hover:text-white hover:border-white/40 transition-colors disabled:opacity-50"><RotateCcw size={13} /> Reset Upload</button>
              <button data-testid="bulk-hide" onClick={() => bulkVisibility(selArr, true)} disabled={bulkBusy}
                className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-2 rounded-sm border border-[#C41E24]/40 text-[#C41E24] hover:bg-[#C41E24] hover:text-white transition-colors disabled:opacity-50"><EyeOff size={13} /> Hide</button>
              <button data-testid="bulk-restore" onClick={() => bulkVisibility(selArr, false)} disabled={bulkBusy}
                className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-2 rounded-sm border border-white/15 text-white/70 hover:text-white hover:border-white/40 transition-colors disabled:opacity-50"><Eye size={13} /> Restore</button>
              <button data-testid="bulk-clear" onClick={clearSelection} className="text-white/40 hover:text-white p-1.5"><X size={14} /></button>
            </div>
          )}
        </div>
      <div className="space-y-3" data-testid="players-list">
        {ordered.map((p) => {
          const inFamily = householdSize[hOf(p)] > 1;
          return (
          <div key={p.id} data-testid={`coach-player-row-${p.id}`}
            className={`w-full flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 border rounded-lg p-4 transition-colors ${p.is_child && inFamily ? "sm:ml-6 bg-[#111] border-l-2 border-l-[#D4AF37]/50" : "bg-[#141414]"} ${selected.has(p.id) ? "border-[#C41E24]" : p.needs_review ? "border-[#C41E24]/50" : "border-white/10 hover:border-[#C41E24]/50"}`}>
            <div className="flex items-center gap-3 w-full sm:w-auto">
            <button data-testid={`bulk-select-${p.id}`} onClick={() => toggleSelect(p.id)} className="text-white/40 hover:text-white flex-shrink-0" aria-label="Select player">
              {selected.has(p.id) ? <CheckSquare size={18} className="text-[#C41E24]" /> : <Square size={18} />}
            </button>
            <button data-testid={`coach-player-${p.id}`} onClick={() => open(p.id)} className="flex items-center gap-4 flex-1 min-w-0 text-left">
              <div className="relative flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-[#C41E24]/15 text-[#C41E24] font-bold flex items-center justify-center">{(p.name || p.email)[0].toUpperCase()}</div>
                {p.needs_review && (
                  <span data-testid={`needs-review-dot-${p.id}`} className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5" title="Pending video you haven't reviewed yet">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C41E24] opacity-75" />
                    <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-[#C41E24] border-2 border-[#141414]" />
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="truncate max-w-full">{p.name || "Unnamed player"}</span>
                  {inFamily && !p.is_child && <span data-testid={`family-badge-${p.id}`} className="text-[9px] font-bold uppercase tracking-wider text-[#D4AF37] bg-[#D4AF37]/15 px-1.5 py-0.5 rounded flex items-center gap-1"><Users size={9} /> Family · {householdSize[hOf(p)]}</span>}
                  {p.is_child && <span data-testid={`sibling-badge-${p.id}`} className="text-[9px] font-bold uppercase tracking-wider text-[#D4AF37]/80 bg-[#D4AF37]/10 px-1.5 py-0.5 rounded">Sibling</span>}
                  {p.needs_review && <span className="text-[9px] font-bold uppercase tracking-wider text-[#C41E24] bg-[#C41E24]/15 px-1.5 py-0.5 rounded">Needs Review</span>}
                  {!!p.bonus_points && <span data-testid={`bonus-badge-${p.id}`} className="text-[9px] font-bold uppercase tracking-wider text-[#D4AF37] bg-[#D4AF37]/15 px-1.5 py-0.5 rounded flex items-center gap-1"><Award size={9} /> +{p.bonus_points}</span>}
                  {p.share_consent ? <span data-testid={`consent-badge-${p.id}`} className="text-[9px] font-bold uppercase tracking-wider text-[#22C55E] bg-[#22C55E]/15 px-1.5 py-0.5 rounded">Consent</span>
                    : <span data-testid={`no-consent-badge-${p.id}`} className="text-[9px] font-bold uppercase tracking-wider text-[#ff8a8e] bg-[#C41E24]/15 px-1.5 py-0.5 rounded">No consent</span>}
                  {p.hidden_from_leaderboard && <span className="text-[9px] font-bold uppercase tracking-wider text-white/50 bg-white/10 px-1.5 py-0.5 rounded">Hidden</span>}
                </div>
                <div className="text-xs text-white/45 truncate">{p.email}</div>
                <div data-testid={`last-active-${p.id}`} className="text-[11px] text-white/35 mt-0.5 flex items-center gap-1"><Clock size={10} /> Active {relTime(p.last_active)}</div>
              </div>
              <div className="hidden sm:flex items-center gap-4 text-xs text-white/55">
                <span>{p.chapters_completed}/{CHAPTERS.length} ch</span>
                <span>{p.journal_count} logs</span>
                <span className="flex items-center gap-1">{p.proofs_count} vids {p.pending_proofs > 0 && <span className="w-1.5 h-1.5 rounded-full bg-[#EAB308]" />}</span>
                {p.can_upload === false
                  ? <span data-testid={`upload-locked-${p.id}`} className="text-[9px] font-bold uppercase tracking-wider text-[#C41E24] bg-[#C41E24]/15 px-1.5 py-0.5 rounded">Upload locked</span>
                  : <span className="text-[10px] text-white/40">{p.uploads_used}/{p.upload_limit} free</span>}
              </div>
            </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto justify-end border-t border-white/10 pt-3 sm:pt-0 sm:border-t-0">
            <button
              data-testid={`pin-onfield-${p.id}`}
              onClick={() => togglePin(p)}
              disabled={pinning === p.id}
              title={p.pinned_today ? "Pinned for today's On-Field session — click to unpin" : "Pin for today's On-Field session"}
              className={`flex-shrink-0 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-2 rounded-sm border transition-colors disabled:opacity-50 ${p.pinned_today ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/15"}`}
            >
              {p.pinned_today ? <PinOff size={13} /> : <Pin size={13} />} <span className="inline">{p.pinned_today ? "Pinned" : "Pin On-Field"}</span>
            </button>
            <SessionCompleteButton token={token} player={p} onDone={(d) => setPlayers((prev) => prev.map((x) => (x.id === p.id ? { ...x, bonus_points: (x.bonus_points || 0) + d.points } : x)))} />
            <HighlightUploadButton token={token} player={p} compact />
            {!p.activated && (
              <button
                data-testid={`copy-setup-link-${p.id}`}
                onClick={() => copySetupLink(p)}
                disabled={copying === p.id}
                title="Copy this player's account setup link"
                className="flex-shrink-0 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-2 rounded-sm border border-[#EAB308]/50 text-[#EAB308] hover:bg-[#EAB308] hover:text-black transition-colors disabled:opacity-50"
              >
                <Link2 size={13} /> <span className="inline">{copying === p.id ? "Copying…" : "Setup Link"}</span>
              </button>
            )}
            <button
              data-testid={`reset-upload-limit-${p.id}`}
              onClick={() => resetUpload(p)}
              disabled={resetting === p.id}
              title="Reset this player's upload limit so they can submit another video for review"
              className="flex-shrink-0 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-2 rounded-sm border border-white/15 text-white/60 hover:text-white hover:border-white/40 transition-colors disabled:opacity-50"
            >
              <RotateCcw size={13} /> <span className="inline">{resetting === p.id ? "Resetting…" : "Reset Upload"}</span>
            </button>
            <button
              data-testid={`toggle-leaderboard-${p.id}`}
              onClick={() => toggleLeaderboard(p)}
              title={p.hidden_from_leaderboard ? "Restore to leaderboard" : "Remove from leaderboard"}
              className={`flex-shrink-0 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-2 rounded-sm border transition-colors ${p.hidden_from_leaderboard ? "border-white/15 text-white/60 hover:text-white hover:border-white/40" : "border-[#C41E24]/40 text-[#C41E24] hover:bg-[#C41E24] hover:text-white"}`}
            >
              {p.hidden_from_leaderboard ? <><Eye size={13} /> <span className="inline">Restore</span></> : <><EyeOff size={13} /> <span className="inline">Remove</span></>}
            </button>
            <ChevronRight size={16} className="text-white/30 flex-shrink-0 hidden sm:block" />
            </div>
          </div>
          );
        })}
      </div>
      </>
        );
      })()}
      </>)}

      {bonusOpen && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={() => setBonusOpen(false)}>
          <div className="bg-[#141414] border border-[#D4AF37]/30 rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="bonus-points-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2"><Award size={18} className="text-[#D4AF37]" /><h3 className="font-headings text-xl uppercase tracking-wide">Award Bonus Points</h3></div>
              <button onClick={() => setBonusOpen(false)} data-testid="bonus-close" className="text-white/50 hover:text-white"><X size={20} /></button>
            </div>
            <p className="text-sm text-white/50 mb-4">Give a leaderboard point boost to the <b className="text-white/80">{selected.size}</b> selected player{selected.size === 1 ? "" : "s"}. Use a negative number to deduct.</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {[10, 25, 50, 100].map((v) => (
                <button key={v} data-testid={`bonus-chip-${v}`} onClick={() => setBonusVal(v)}
                  className={`text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-full border transition-colors ${bonusVal === v ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-white/5 border-white/15 text-white/60 hover:text-white"}`}>+{v}</button>
              ))}
            </div>
            <input data-testid="bonus-input" type="number" value={bonusVal} onChange={(e) => setBonusVal(e.target.value)}
              className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3 text-white text-sm mb-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]" placeholder="Points (e.g. 25)" />
            <button data-testid="bonus-apply" onClick={() => awardBonus([...selected], bonusVal)} disabled={bulkBusy}
              className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#c39f2f] text-black text-sm font-bold uppercase tracking-wider py-3 rounded-sm transition-colors disabled:opacity-60">
              <Award size={15} /> {bulkBusy ? "Applying…" : `Award to ${selected.size} player${selected.size === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      )}

      {active && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-2 sm:p-4" onClick={closeDetail}>
          <div className="bg-[#141414] border border-white/10 rounded-xl max-w-3xl w-full my-3 sm:my-8 p-4 sm:p-6" onClick={(e) => e.stopPropagation()} data-testid="coach-player-detail">
            <div className="flex items-start justify-between gap-3 mb-6">
              <div className="min-w-0">
                <h3 className="font-headings text-2xl uppercase tracking-wide truncate">{active.player.name || "Unnamed player"}</h3>
                <p className="text-sm text-white/45 truncate">{active.player.email}</p>
              </div>
              <button data-testid="coach-player-detail-close" onClick={closeDetail} aria-label="Close" className="shrink-0 text-white/50 hover:text-white p-2 -m-2"><X size={20} /></button>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-[#0d0d0d] border border-white/10 rounded-md p-3 text-center"><div className="font-cover text-3xl text-[#C41E24]">{active.completed_chapters.length}</div><div className="text-[10px] uppercase tracking-wider text-white/40">Chapters</div></div>
              <div className="bg-[#0d0d0d] border border-white/10 rounded-md p-3 text-center"><div className="font-cover text-3xl text-[#C41E24]">{active.journal_entries.length}</div><div className="text-[10px] uppercase tracking-wider text-white/40">Journal Logs</div></div>
              <div className="bg-[#0d0d0d] border border-white/10 rounded-md p-3 text-center"><div className="font-cover text-3xl text-[#C41E24]">{active.proofs.length}</div><div className="text-[10px] uppercase tracking-wider text-white/40">Video Proofs</div></div>
            </div>

            {/* Membership tier + Elite personal greeting */}
            <div className="bg-[#0d0d0d] border border-[#D4AF37]/25 rounded-lg p-4 mb-6" data-testid="coach-membership-card">
              <div className="flex items-center gap-2 mb-3"><Crown size={15} className="text-[#D4AF37]" /><h4 className="font-subheadings text-xs font-bold uppercase tracking-widest text-white/70">Membership Tier</h4></div>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                <div className="flex-1">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-1.5 block">Tier</label>
                  <select data-testid="coach-tier-select" value={tierDraft} onChange={(e) => setTierDraft(e.target.value)}
                    className="w-full bg-[#141414] border border-white/10 rounded-md p-2.5 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]">
                    <option value="ebook">Blueprint — Tier 1</option>
                    <option value="academy">Technical Track — Tier 2</option>
                    <option value="elite">Elite — Tier 3</option>
                  </select>
                </div>
                <button data-testid="coach-save-membership" onClick={saveMembership} disabled={savingTier}
                  className="flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#c39f2f] text-black text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm transition-colors disabled:opacity-60">
                  <Save size={14} /> {savingTier ? "Saving…" : "Save Membership"}
                </button>
              </div>
              <VipPackageForm token={token} player={{ ...active.player, vip_until: players.find((x) => x.id === active.player.id)?.vip_until }}
                onGranted={(until) => setPlayers((prev) => prev.map((x) => (x.id === active.player.id ? { ...x, vip_until: until, tier: until ? "elite" : x.tier } : x)))} />
              <TechnicalRatings token={token} playerId={active.player.id} initial={active.orank} />
              <HomeworkPanel token={token} player={active.player} />
              <SessionLogPanel token={token} player={active.player} />
              <DossierRelease token={token} player={active.player} />
              {tierDraft === "elite" && (
                <div className="mt-3" data-testid="coach-greeting-field">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-[#D4AF37] mb-1.5 block">Personalized Greeting — YouTube link (unique to this player)</label>
                  <input data-testid="coach-greeting-input" type="url" value={greetingDraft} onChange={(e) => setGreetingDraft(e.target.value)}
                    className="w-full bg-[#141414] border border-white/10 rounded-md p-2.5 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]"
                    placeholder="https://youtube.com/watch?v=… (blank = hidden)" />
                  <p className="text-[11px] text-white/45 mt-1.5">Saving a new link emails this player that a personal message from Coach Barker is waiting.</p>

                  <div className="mt-4 pt-4 border-t border-white/10" data-testid="coach-roadmap-fields">
                    <div className="flex items-center gap-2 mb-3"><span className="text-[10px] font-bold uppercase tracking-widest text-[#D4AF37]">Monthly Roadmap</span></div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-1.5 block">Month label (e.g. June 2026)</label>
                    <input data-testid="coach-roadmap-month" type="text" value={roadmap.month} onChange={(e) => setRoadmap((r) => ({ ...r, month: e.target.value }))}
                      className="w-full bg-[#141414] border border-white/10 rounded-md p-2.5 text-white text-sm mb-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]" placeholder="June 2026" />
                    <label className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-1.5 block">Monthly Focus Goals</label>
                    <textarea data-testid="coach-roadmap-focus" rows={3} value={roadmap.focus} onChange={(e) => setRoadmap((r) => ({ ...r, focus: e.target.value }))}
                      className="w-full bg-[#141414] border border-white/10 rounded-md p-2.5 text-white text-sm mb-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]" placeholder="e.g. Sharpen weak-foot finishing; add 2 new dribble moves under pressure." />
                    <label className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-1.5 block">Recommended Drill Schedule</label>
                    <textarea data-testid="coach-roadmap-schedule" rows={4} value={roadmap.schedule} onChange={(e) => setRoadmap((r) => ({ ...r, schedule: e.target.value }))}
                      className="w-full bg-[#141414] border border-white/10 rounded-md p-2.5 text-white text-sm mb-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]" placeholder="Mon: Ball mastery · Wed: 1v1 moves · Fri: Finishing" />
                    <label className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-1.5 block">Monthly Performance Summary</label>
                    <textarea data-testid="coach-roadmap-summary" rows={3} value={roadmap.summary} onChange={(e) => setRoadmap((r) => ({ ...r, summary: e.target.value }))}
                      className="w-full bg-[#141414] border border-white/10 rounded-md p-2.5 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]" placeholder="How they progressed last month, wins, and what to work on next." />
                  </div>
                </div>
              )}
            </div>

            {/* Per-chapter progress chart */}
            {(() => {
              const done = new Set(active.completed_chapters);
              const pct = Math.round((done.size / CHAPTERS.length) * 100);
              return (
                <div className="mb-6" data-testid="coach-progress-chart">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-subheadings text-xs font-bold uppercase tracking-widest text-white/70">Chapter Progress</h4>
                    <span className="text-xs font-bold text-[#C41E24]">{pct}% complete</span>
                  </div>
                  <div className="h-2 w-full bg-[#0d0d0d] border border-white/10 rounded-full overflow-hidden mb-4">
                    <div className="h-full bg-[#C41E24] transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {CHAPTERS.map((c, i) => {
                      const complete = done.has(c.id);
                      return (
                        <div key={c.id} data-testid={`coach-chapter-${c.id}`}
                          className={`flex items-center gap-2 rounded-md px-3 py-2 border text-xs ${complete ? "bg-[#C41E24]/10 border-[#C41E24]/40" : "bg-[#0d0d0d] border-white/10"}`}>
                          {complete
                            ? <CheckCircle2 size={14} className="text-[#22C55E] flex-shrink-0" />
                            : <div className="w-3.5 h-3.5 rounded-full border border-white/25 flex-shrink-0" />}
                          <span className={`truncate ${complete ? "text-white" : "text-white/45"}`}>{i + 1}. {c.title}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Video proofs review */}
            <h4 className="font-subheadings text-xs font-bold uppercase tracking-widest text-white/70 mb-3">Video Proofs</h4>
            <div className="space-y-4 mb-6">
              {active.proofs.length === 0 && <p className="text-sm text-white/40">No video proofs uploaded yet.</p>}
              {active.proofs.map((pr) => {
                const st = STATUS[pr.status] || STATUS.pending;
                return (
                  <div key={pr.id} className="bg-[#0d0d0d] border border-white/10 rounded-lg p-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <video controls preload="none" className="w-full aspect-video bg-black rounded" src={`${API}/proofs/file/${pr.id}?auth=${token}`} />
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-sm">{pr.move_label || "Move"}</span>
                          <span className="text-[10px] font-bold uppercase tracking-wider flex items-center gap-1" style={{ color: st.color }}><st.icon size={12} /> {st.label}</span>
                        </div>
                        <textarea id={`note-${pr.id}`} defaultValue={pr.coach_note} rows={2} placeholder="Feedback for the player…"
                          className="w-full bg-[#141414] border border-white/10 rounded-md p-2 text-xs text-white mb-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]" />
                        <div className="flex gap-2">
                          <button data-testid={`approve-${pr.id}`} onClick={() => review(pr.id, "approved", document.getElementById(`note-${pr.id}`).value)} className="flex-1 bg-green-600/80 hover:bg-green-600 text-white text-xs font-bold uppercase tracking-wider py-2 rounded-sm">Approve</button>
                          <button data-testid={`needswork-${pr.id}`} onClick={() => review(pr.id, "needs_work", document.getElementById(`note-${pr.id}`).value)} className="flex-1 bg-[#C41E24] hover:bg-[#A0161D] text-white text-xs font-bold uppercase tracking-wider py-2 rounded-sm">Needs Work</button>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3">
                      <EngineSummary proof={pr} onOpen={() => setEngineOpen(engineOpen === pr.id ? null : pr.id)} />
                      {engineOpen === pr.id && pr.engine && (
                        <div className="mt-3 rounded-lg border border-[#D4AF37]/20 bg-black/40 p-3">
                          <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-white/40 mb-3">Engine draft — review before your voice note</p>
                          <EngineReport proof={pr} showVideo={false} token={token} api={API} player={active.player}
                            onCueTap={(text) => startVoiceFromCue(pr, text)}
                            history={active.proofs.filter((x) => x.engine && (pr.move_id ? x.move_id === pr.move_id : x.id === pr.id))} />
                        </div>
                      )}
                    </div>
                    <ProofReaction proof={pr} token={token} talkingPoint={talkPoint[pr.id]} onSaved={() => { open(active.player.id); load(); }} />
                  </div>
                );
              })}
            </div>

            {/* Journal history */}
            <h4 className="font-subheadings text-xs font-bold uppercase tracking-widest text-white/70 mb-3">Journal History</h4>
            <div className="space-y-2">
              {active.journal_entries.length === 0 && <p className="text-sm text-white/40">No journal entries yet.</p>}
              {active.journal_entries.map((e) => (
                <div key={e.id || e.date} className="bg-[#0d0d0d] border border-white/10 rounded-md p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-[#C41E24]">{e.date}</span>
                    <span className="text-xs text-white/50 flex items-center gap-1"><Footprints size={11} className="text-[#C41E24]" /> {e.weakFoot}/10</span>
                  </div>
                  <div className="text-xs text-white/70 space-y-0.5">
                    {e.focus && <div><b className="text-white/45">Focus:</b> {e.focus}</div>}
                    {e.challenge && <div><b className="text-white/45">Challenge:</b> {e.challenge}</div>}
                    {e.result && <div><b className="text-white/45">Result:</b> {e.result}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
